import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Info, Languages, ScanLine } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Field, InlineAlert, NativeSelect, Skeleton, Textarea, cn, useSave } from "@ncp/ui";

/** How one extracted value was read (substantive review 6.12): its page, whether that page was a scan, and a confidence. */
export interface ValueReading { field: string; value: string; page?: number | null; scanned: boolean; confidence: number; quote?: string | null; lowConfidence?: boolean; toBeChecked?: boolean }
/** How a document's text was read: pages in the file, scanned, not read, read in part, not stored as text; each value's reading. */
export interface DocumentReadingInfo { format: string; filePages: number; scannedPages: number[]; unreadPages: number[]; partlyReadPages: number[]; storedThroughPage?: number | null; values: ValueReading[]; readBy?: AiReadingInfo | null; checkedBy?: ReadingCheckInfo | null }
/** What read the document (Condition 3.7): the AI service, its model under the platform's name and the version of its instructions. */
export interface AiReadingInfo { agent: string; model: string; instructionVersion?: string | null; transcriptionModel?: string | null; transcriptionInstructionVersion?: string | null; at: string }
/** The officer who checked the AI's reading against the document (Condition 3.1). */
export interface ReadingCheckInfo { userId?: string | null; name: string; at: string; note?: string | null }
export interface DocumentCheck { code: string; severity: "Positive" | "Info" | "Warning"; en: string; ar: string }

const toCheck = (v: ValueReading) => v.toBeChecked ?? (v.scanned || v.confidence < 0.75);

/** "25 pages in the file · 24 transcribed from a scan · 1 not read": what the reading did with the file's pages. */
export function ReadingSummary({ reading }: { reading?: DocumentReadingInfo | null }) {
  const { t } = useTranslation();
  if (!reading || reading.filePages <= 0) return null;
  const notStored = reading.storedThroughPage != null ? Math.max(0, reading.filePages - reading.storedThroughPage) : 0;
  const parts = [
    t("backoffice:workspace.documentReading.pages", { count: reading.filePages }),
    reading.scannedPages.length ? t("backoffice:workspace.documentReading.scanned", { count: reading.scannedPages.length }) : null,
    reading.unreadPages.length ? t("backoffice:workspace.documentReading.unread", { count: reading.unreadPages.length }) : null,
    reading.partlyReadPages.length ? t("backoffice:workspace.documentReading.partly", { count: reading.partlyReadPages.length }) : null,
    notStored ? t("backoffice:workspace.documentReading.notStored", { count: notStored }) : null,
  ].filter(Boolean);
  const warn = reading.unreadPages.length > 0 || reading.partlyReadPages.length > 0 || notStored > 0;
  return <p className={cn("text-xs", warn ? "text-warning-800" : "text-sand-800")} data-reading={reading.format}>{parts.join(" · ")}</p>;
}

/** Tags for the document list: a scan, and pages that could not be read. */
export function readingTags(reading: DocumentReadingInfo | null | undefined, t: (k: string) => string) {
  if (!reading) return [];
  return [
    reading.scannedPages.length ? <Badge key="scan" size="sm" variant="outline"><ScanLine /> {t("backoffice:workspace.documentReading.scanTag")}</Badge> : null,
    reading.unreadPages.length ? <Badge key="unread" size="sm" variant="warning"><AlertTriangle /> {t("backoffice:workspace.documentReading.unreadTag")}</Badge> : null,
  ].filter(Boolean);
}

/** The document's checks, in the officer's language: pages not read, scans, values to be checked, the translation, and the rest. */
export function DocumentChecksList({ checks }: { checks?: DocumentCheck[] | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!checks) return null;
  const points = checks.filter((c) => c.severity !== "Positive");
  return (
    <div className="mt-4 space-y-2" data-document-checks>
      <p className="text-xs font-semibold text-sand-800">{t("backoffice:workspace.documentReading.checks")}</p>
      {points.length === 0 ? <p className="text-xs text-muted-foreground">{t("backoffice:workspace.documentReading.noChecks")}</p> : (
        <ul className="space-y-1.5">
          {points.map((c) => {
            const Icon = c.severity === "Warning" ? AlertTriangle : Info;
            return (
              <li key={c.code} data-check={c.code} data-severity={c.severity} className={cn("flex items-start gap-2 rounded-lg border bg-white p-2 text-xs leading-relaxed", c.severity === "Warning" ? "border-warning-200" : "border-sand-200")}>
                <Icon className={cn("mt-0.5 size-3.5 shrink-0", c.severity === "Warning" ? "text-warning-700" : "text-seablue-700")} />
                <span>{locale === "ar" ? c.ar : c.en}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** "To be checked · page 7 · scanned page" beside an extracted value read from a scan or with low confidence. */
export function ReadingMark({ readings }: { readings: ValueReading[] }) {
  const { t } = useTranslation();
  const flagged = readings.filter(toCheck);
  if (flagged.length === 0) return null;
  const r = flagged[0];
  const why = [
    r.page != null ? t("backoffice:workspace.documentReading.onPage", { page: r.page }) : t("backoffice:workspace.documentReading.notFound"),
    r.scanned ? t("backoffice:workspace.documentReading.scannedPage") : r.page != null ? t("backoffice:workspace.documentReading.lowConfidence") : null,
  ].filter(Boolean).join(" · ");
  return <span className="ms-2 inline-flex items-center gap-1 rounded-md border border-warning-200 bg-warning-50 px-1.5 text-[11px] leading-5 text-warning-800" data-to-be-checked={r.field} title={r.quote ?? undefined}><ScanLine className="size-3" /> {t("backoffice:workspace.documentReading.toCheck")} · {why}</span>;
}

/** The readings of one extracted field, or of one item of a listed field (matched by its values). */
export function readingsFor(readings: ValueReading[] | undefined, field: string, item?: unknown): ValueReading[] {
  if (!readings?.length) return [];
  const mine = readings.filter((r) => r.field === field || r.field.startsWith(`${field}.`));
  if (item === undefined) return mine;
  const values = new Set((item !== null && typeof item === "object" ? Object.values(item as Record<string, unknown>) : [item]).filter((v) => typeof v === "string" || typeof v === "number").map(String));
  return mine.filter((r) => values.has(r.value));
}

interface TranslationDifference { kind: "figure" | "date" | "article" | "name" | "term"; arabic?: string | null; english?: string | null; en: string; ar: string }
interface TranslationView {
  paired: boolean;
  original?: { id: string; fileName: string; language: string };
  translation?: { id: string; fileName: string; language: string };
  inferred?: boolean; arabicPrevails?: boolean; alignment?: "pages" | "articles" | "whole"; differences?: number;
  passages?: Array<{ index: number; arabicPage?: number | null; englishPage?: number | null; article?: string | null; arabic: string; english: string; differences: TranslationDifference[] }>;
  task?: { id: string; status: string; completedAt?: string | null; decidedBy?: string | null; decidedByAr?: string | null; reason?: string | null; decision?: string | null } | null;
}

/**
 * A document and its translation side by side (substantive review 6.13): the Arabic and the English passage by passage, each passage with
 * its differences marked; for legislation and the Ministry's documents the Arabic text prevails; a party document's discrepancy is the
 * officer's to decide, with reasons, here – the referral task closes with that decision and both go to the journal.
 */
export function TranslationPanel({ caseId, documentId, documents }: { caseId: string; documentId: string; documents: Array<{ id: string; fileName: string; language: string; translationOfId?: string | null }> }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const view = useQuery({ queryKey: ["doc-translation", caseId, documentId], queryFn: () => api.get<TranslationView>(`/api/cases/${caseId}/documents/${documentId}/translation`) });
  const [original, setOriginal] = useState("");
  const [decision, setDecision] = useState("arabic");
  const [reason, setReason] = useState("");
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["doc-translation", caseId] }); void qc.invalidateQueries({ queryKey: ["case-documents", caseId] }); };
  const link = useSave({
    mutationFn: (originalId: string | null) => api.put(`/api/cases/${caseId}/documents/${documentId}/translation`, { originalId }),
    saved: t("backoffice:workspace.documentTranslation.linked"),
    onSuccess: () => { refresh(); },
  });
  const decide = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/documents/${documentId}/translation/decision`, { decision, reason }),
    saved: t("backoffice:workspace.documentTranslation.saved"),
    onSuccess: () => { setReason(""); refresh(); },
  });

  if (view.isLoading) return <Skeleton className="h-48" />;
  const v = view.data;
  const self = documents.find((d) => d.id === documentId);
  if (!v?.paired) {
    const candidates = documents.filter((d) => d.id !== documentId && d.language !== self?.language);
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t("backoffice:workspace.documentTranslation.none")}</p>
        {candidates.length ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t("backoffice:workspace.documentTranslation.linkLabel")} className="min-w-64 flex-1">
              {(id) => (
                <NativeSelect id={id} value={original} onChange={(e) => setOriginal(e.target.value)}>
                  <option value="">{t("backoffice:workspace.documentTranslation.choose")}</option>
                  {candidates.map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
                </NativeSelect>
              )}
            </Field>
            <Button size="sm" disabled={!original} loading={link.isPending} onClick={() => link.mutate(original)}><Languages /> {t("backoffice:workspace.documentTranslation.link")}</Button>
          </div>
        ) : null}
      </div>
    );
  }

  const open = v.task && (v.task.status === "Open" || v.task.status === "InProgress");
  const decided = v.task?.status === "Completed";
  const decisionText = (d?: string | null) => t(`backoffice:workspace.documentTranslation.decision.${d === "english" ? "english" : d === "corrected" ? "corrected" : "arabic"}`);
  const where = (p: NonNullable<TranslationView["passages"]>[number]) => p.article ? t("backoffice:workspace.documentTranslation.article", { article: p.article })
    : p.arabicPage != null && p.englishPage != null ? (p.arabicPage === p.englishPage ? t("backoffice:workspace.documentTranslation.page", { page: p.arabicPage }) : t("backoffice:workspace.documentTranslation.pages", { ar: p.arabicPage, en: p.englishPage }))
    : t("backoffice:workspace.documentTranslation.whole");
  const lang = (l: string) => t(`backoffice:workspace.documentTranslation.${l === "ar" ? "arabic" : "english"}`);

  return (
    <div className="space-y-3" data-translation-pair>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-sand-800">
        <p>
          {t("backoffice:workspace.documentTranslation.original")} ({lang(v.original!.language)}): <bdi className="font-medium">{v.original!.fileName}</bdi>
          {" · "}{t("backoffice:workspace.documentTranslation.translation")} ({lang(v.translation!.language)}): <bdi className="font-medium">{v.translation!.fileName}</bdi>
        </p>
        <div className="flex items-center gap-2">
          <Badge size="sm" variant={v.differences ? "warning" : "outline"}>{v.differences ? t("backoffice:workspace.documentTranslation.differences", { count: v.differences }) : t("backoffice:workspace.documentTranslation.noDifferences")}</Badge>
          {!v.inferred && v.translation!.id === documentId ? <Button size="sm" variant="ghost" loading={link.isPending} onClick={() => link.mutate(null)}>{t("backoffice:workspace.documentTranslation.unlink")}</Button> : null}
        </div>
      </div>
      {v.inferred ? <p className="text-xs text-muted-foreground">{t("backoffice:workspace.documentTranslation.inferred")}</p> : null}
      {v.arabicPrevails ? <InlineAlert tone="info">{t("backoffice:workspace.documentTranslation.arabicPrevails")}</InlineAlert>
        : decided ? (
          <InlineAlert tone="success" title={t("backoffice:workspace.documentTranslation.decided", { name: (ar ? v.task!.decidedByAr : null) ?? v.task!.decidedBy ?? "", date: v.task!.completedAt ? formatDate(v.task!.completedAt, locale) : "", decision: decisionText(v.task!.decision) })}>
            <p className="mt-1 whitespace-pre-wrap text-xs" dir="auto"><span className="font-medium">{t("backoffice:workspace.documentTranslation.reasons")}:</span> {v.task!.reason}</p>
          </InlineAlert>
        )
        : v.differences ? <InlineAlert tone="warning">{t("backoffice:workspace.documentTranslation.referred")}</InlineAlert> : null}

      <div className="space-y-3">
        {v.passages!.map((p) => (
          <section key={p.index} className={cn("rounded-xl border bg-white", p.differences.length ? "border-warning-300" : "border-sand-200")} data-passage={p.index} data-differences={p.differences.length}>
            <p className="border-b border-sand-100 px-3 py-1.5 text-[11px] font-semibold text-sand-800">{where(p)}</p>
            <div className="grid gap-0 @3xl:grid-cols-2">
              <div dir="rtl" lang="ar" className="max-h-72 overflow-auto whitespace-pre-wrap p-3 text-xs leading-relaxed @3xl:border-e @3xl:border-sand-100">
                {v.arabicPrevails ? <Badge size="sm" variant="outline" className="mb-1">{t("backoffice:workspace.documentTranslation.arabic")} · {t("backoffice:workspace.documentTranslation.original")}</Badge> : null}
                {p.arabic || <span className="text-muted-foreground">{t("backoffice:workspace.documentTranslation.empty")}</span>}
              </div>
              <div dir="ltr" lang="en" className="max-h-72 overflow-auto whitespace-pre-wrap p-3 text-xs leading-relaxed">{p.english || <span className="text-muted-foreground">{t("backoffice:workspace.documentTranslation.empty")}</span>}</div>
            </div>
            {p.differences.length ? (
              <ul className="space-y-1 border-t border-warning-200 bg-warning-50/50 px-3 py-2">
                {p.differences.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs" data-difference={d.kind}>
                    <Badge size="sm" variant="warning">{t(`backoffice:workspace.documentTranslation.kind.${d.kind}`)}</Badge>
                    <span className="min-w-0 flex-1">{ar ? d.ar : d.en}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>

      {open ? (
        <div className="space-y-2 rounded-xl border border-sand-200 bg-sand-50/60 p-3">
          <Field label={t("backoffice:workspace.documentTranslation.decisionLabel")} required>
            {(id) => (
              <NativeSelect id={id} value={decision} onChange={(e) => setDecision(e.target.value)}>
                {(["arabic", "english", "corrected"] as const).map((d) => <option key={d} value={d}>{decisionText(d)}</option>)}
              </NativeSelect>
            )}
          </Field>
          <Field label={t("backoffice:workspace.documentTranslation.reasons")} hint={t("backoffice:workspace.documentTranslation.reasonsHint")} required>
            {(id) => <Textarea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} dir="auto" draft={`case:${caseId}:document:${documentId}:translation:reason`} />}
          </Field>
          <div className="flex justify-end">
            <Button size="sm" disabled={reason.trim().length < 10} loading={decide.isPending} onClick={() => decide.mutate()}><CheckCircle2 /> {t("backoffice:workspace.documentTranslation.decide")}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The AI's reading of a document – its summary and the fields read from it – marked as the AI's, with the model and instruction version, until an
 * officer records that they checked it against the document (Condition 3.1); then the officer's name and the date. A new reading clears the check.
 */
export function AiReadingBanner({ caseId, documentId, reading }: { caseId: string; documentId: string; reading?: DocumentReadingInfo | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const check = useSave({
    mutationFn: () => api.post<{ checkedBy: ReadingCheckInfo }>(`/api/cases/${caseId}/documents/${documentId}/reading/check`, {}),
    expect: "record",
    saved: t("backoffice:workspace.documentReading.checkRecorded"),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["case-documents", caseId] }),
  });
  const by = reading?.readBy;
  if (!by) return <p className="mb-3 text-xs text-muted-foreground">{t("backoffice:workspace.documentReading.aiReadingLegacy")}</p>;
  const version = [by.instructionVersion ? t("backoffice:workspace.documentReading.instructionVersion", { version: by.instructionVersion }) : null, by.transcriptionInstructionVersion ? t("backoffice:workspace.documentReading.transcriptionVersion", { version: by.transcriptionInstructionVersion }) : null].filter(Boolean).join(" · ");
  const checked = reading?.checkedBy;
  return (
    <div className={cn("mb-3 flex flex-wrap items-center gap-2 rounded-xl border p-2.5 text-xs", checked ? "border-success-200 bg-success-50/60 text-success-900" : "border-seablue-200 bg-seablue-50/50 text-seablue-900")} data-ai-reading={checked ? "checked" : "unchecked"}>
      <Badge size="sm" variant="ai">{t("backoffice:workspace.documentReading.aiReading")}</Badge>
      <span className="font-mono" dir="ltr">{by.model}</span>
      {version ? <span>{version}</span> : null}
      <span className="min-w-0 flex-1">{checked ? t("backoffice:workspace.documentReading.checkedBy", { name: checked.name, date: formatDate(checked.at, locale) }) : t("backoffice:workspace.documentReading.notChecked")}</span>
      {!checked ? <Button size="sm" variant="outline" loading={check.isPending} onClick={() => check.mutate()}><CheckCircle2 /> {t("backoffice:workspace.documentReading.check")}</Button> : null}
    </div>
  );
}
